import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, Box, Grid, TextField, IconButton, Tooltip, Snackbar, Alert, ClickAwayListener, Fade, Grow, CircularProgress, useTheme, useMediaQuery, Paper, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { useDashboard } from './Dashboard/DashboardContext';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../Utils/apiClient';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';
import { hasUserTagVariableReservedChars } from '../Utils/tagUtils';

// Theme colors matching TimeEventTag
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';

const StyledTagPaper = styled(Paper)(({ theme, backgroundColor }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  background: `linear-gradient(135deg, white 0%, ${backgroundColor || lightPurple}15 100%)`,
  border: 'none',
  borderLeft: `6px solid ${backgroundColor || primaryColor}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(87, 87, 176, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, white 0%, ${backgroundColor || lightPurple}25 100%)`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${backgroundColor || primaryColor}08 0%, transparent 100%)`,
    pointerEvents: 'none',
  }
}));

const SettingIntelligentTag = ({ onTagsChange, onTagsSaved }) => {
  const { caseid } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [editableTags, setEditableTags] = useState([]);
  const [originalTags, setOriginalTags] = useState([]);
  const [isChanged, setIsChanged] = useState(false);
  const [hasEverChanged, setHasEverChanged] = useState(false);  // Track if changes were ever made
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});
  const [expandedFields, setExpandedFields] = useState({});
  // Animation states
  const [isLoaded, setIsLoaded] = useState(false);
  const [animatingDelete, setAnimatingDelete] = useState({});
  const [savingIndex, setSavingIndex] = useState(null);
  const { tags, setTags } = useDashboard();
  const skipNextTagsSyncRef = useRef(false);

  useEffect(() => {
    if (tags) {
      if (skipNextTagsSyncRef.current) {
        skipNextTagsSyncRef.current = false;
        return;
      }

      try {
        const parsedTags = JSON.parse(tags);
        setEditableTags(parsedTags);
        // Store original tags for later comparison
        setOriginalTags(JSON.parse(JSON.stringify(parsedTags)));
        setIsChanged(false);
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('settings.tags_parse_error'),
          severity: 'error'
        });
        console.error('Error parsing tags:', error);
      }
    }
  }, [tags, t]);

  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Notify parent component when tags change
  useEffect(() => {
    // Determine if tags have structurally changed
    const checkTagsChanged = () => {
      if (editableTags.length !== originalTags.length) return true;
      
      // Check for any differences in the tags
      for (let i = 0; i < editableTags.length; i++) {
        if (i >= originalTags.length) return true;
        
        const edited = editableTags[i];
        const original = originalTags[i];
        
        if (edited.variable !== original.variable || 
            edited.description !== original.description) {
          return true;
        }
      }
      
      return false;
    };

    const tagsChanged = checkTagsChanged();
    setIsChanged(tagsChanged);
    if (tagsChanged) {
      setHasEverChanged(true);
    }
    
    if (onTagsChange) {
      onTagsChange(hasEverChanged || tagsChanged);
    }
  }, [editableTags, originalTags, onTagsChange, hasEverChanged]);

  const validateTag = (tag, index) => {
    const newErrors = {};
    let isValid = true;

    if (!tag.variable.trim()) {
      newErrors[`variable-${index}`] = t('settings.variable_required');
      isValid = false;
    } else {
      const originalVariable = (originalTags[index]?.variable || '').trim();
      const editedVariable = tag.variable.trim();
      if (
        editedVariable !== originalVariable
        && hasUserTagVariableReservedChars(editedVariable)
      ) {
        newErrors[`variable-${index}`] = t('settings.variable_no_space_underscore');
        isValid = false;
      }
    }

    if (!tag.description.trim()) {
      newErrors[`description-${index}`] = t('settings.description_required');
      isValid = false;
    }
    
    setErrors(prev => {
      const updatedErrors = { ...prev };
      delete updatedErrors[`variable-${index}`];
      delete updatedErrors[`description-${index}`];
      return { ...updatedErrors, ...newErrors };
    });

    return isValid;
  };

  const hasTagChanged = (index) => {
    const edited = editableTags[index];
    const original = originalTags[index];

    if (!edited) return false;
    if (!original) return true;

    return edited.variable !== original.variable || edited.description !== original.description;
  };

  const buildSingleTagSavePayload = (index) => {
    const tag = editableTags[index];
    const savedTags = JSON.parse(JSON.stringify(originalTags));

    if (index < savedTags.length) {
      savedTags[index] = tag;
      return savedTags;
    }

    return [...savedTags, tag];
  };

  const persistTags = async (tagsToSave, successMessageKey, fallbackMessage, saveIndex = null, displayTags = tagsToSave) => {
    setSavingIndex(saveIndex);

    try {
      await apiRequest(`/api/v1/projects/${encodeURIComponent(caseid)}`, {
        method: 'PATCH',
        body: JSON.stringify({ customFields: tagsToSave }),
      });
      skipNextTagsSyncRef.current = true;
      setTags(JSON.stringify(tagsToSave));
      setEditableTags(displayTags);
      setOriginalTags(JSON.parse(JSON.stringify(tagsToSave)));
      if (onTagsSaved) {
        onTagsSaved();
      }

      setSnackbar({
        open: true,
        message: t(successMessageKey) || fallbackMessage,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to save intelligent tags:', error);
      setSnackbar({
        open: true,
        message: error?.message || t('settings.connection_error'),
        severity: 'error'
      });
    } finally {
      setSavingIndex(null);
    }
  };

  const requestSaveTags = (tagsToSave, saveIndex = null, displayTags = tagsToSave) => {
    return persistTags(tagsToSave, 'settings.tags_saved', 'Tags saved successfully', saveIndex, displayTags);
  };

  const handleTagChange = (index, key, value) => {
    const updatedTags = [...editableTags];
    
    // Trim spaces from variable names
    if (key === 'variable') {
      updatedTags[index][key] = value.trim();
    } else {
      updatedTags[index][key] = value;
    }
    
    setEditableTags(updatedTags);
    
    // Clear error for this field if it exists
    if (errors[`${key}-${index}`]) {
      const updatedErrors = {...errors};
      delete updatedErrors[`${key}-${index}`];
      setErrors(updatedErrors);
    }
  };

  const handleAddTag = () => {
    if (savingIndex !== null || isChanged) {
      return;
    }

    setEditableTags([...editableTags, { variable: '', description: '' }]);
  };

  const handleDeleteTag = (index) => {
    setAnimatingDelete(prev => ({ ...prev, [index]: true }));
    
    setTimeout(() => {
      const updatedTags = editableTags.filter((_, i) => i !== index);
      setEditableTags(updatedTags);
      if (index < originalTags.length) {
        const tagsToSave = originalTags.filter((_, i) => i !== index);
        requestSaveTags(tagsToSave, index, updatedTags);
      }
      setAnimatingDelete(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }, 200);
  };

  const handleSaveTag = (index) => {
    const tag = editableTags[index];

    if (!tag || !validateTag(tag, index)) {
      return;
    }

    requestSaveTags(buildSingleTagSavePayload(index), index, editableTags);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };

  const toggleDescriptionExpand = (index) => {
    setExpandedFields(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleClickAway = (index) => {
    setExpandedFields(prev => ({
      ...prev,
      [index]: false
    }));
  };

  return (
    <Fade in={isLoaded} timeout={600}>
      <Box>
        <Typography variant="h5" gutterBottom>
          {t('settings.intelligent_tags')}
          <Tooltip title={t("settings.manage_intelligent_tags")}>
            <IconButton sx={{ ml: 1, color: primaryColor, opacity: 0.7 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Box sx={{ width: '100%', textAlign: 'center', mt: 1, mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="intelligent-tags-description"
            sx={{ mx: 'auto', maxWidth: { xs: '100%', sm: 600 } }}
          >
            {t("settings.intelligent_tags_description", "Create smart tags to help AI automatically extract and organize information from your documents.")}
          </Typography>
        </Box>

      {editableTags.map((tag, index) => (
        <Grow in={!animatingDelete[index]} timeout={300 + index * 100} key={index}>
          <StyledTagPaper 
            elevation={0} 
            backgroundColor={generatePastelColor(tag.variable || index.toString())}
            sx={{ 
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2.5 },
              mx: 0
            }}
          >
            <Grid container spacing={isMobile ? 1.5 : 3} alignItems={isMobile ? "center" : "flex-start"}>
              <Grid item xs={10} sm={4} sx={{ order: { xs: 1, sm: 1 } }}>
                <TextField
                  label={t('settings.variable')}
                  value={tag.variable}
                  onChange={(e) => handleTagChange(index, 'variable', e.target.value)}
                  fullWidth
                  error={!!errors[`variable-${index}`]}
                  helperText={errors[`variable-${index}`] || ''}
                  required
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <Box 
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '8px',
                          backgroundColor: `${generateSaturatedColor(tag.variable || index.toString())}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: generateSaturatedColor(tag.variable || index.toString()),
                          fontSize: '11px',
                          fontWeight: '800',
                          mr: 1,
                          flexShrink: 0,
                          border: `1px solid ${generateSaturatedColor(tag.variable || index.toString())}30`,
                          textShadow: '0 1px 0 rgba(255,255,255,0.5)'
                        }}
                      >
                        {(tag.variable || 'T').charAt(0).toUpperCase()}
                      </Box>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: primaryColor },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ order: { xs: 3, sm: 2 } }}>
                <ClickAwayListener onClickAway={() => handleClickAway(index)}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      label={t('settings.description')}
                      value={tag.description}
                      onChange={(e) => handleTagChange(index, 'description', e.target.value)}
                      fullWidth
                      multiline
                      rows={expandedFields[index] ? 4 : 1}
                      error={!!errors[`description-${index}`]}
                      helperText={errors[`description-${index}`] || (expandedFields[index] ? t('settings.tag_description_tip') : '')}
                      required
                      onClick={() => !expandedFields[index] && toggleDescriptionExpand(index)}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: !expandedFields[index] && (
                          <DescriptionIcon color="action" sx={{ mr: 1, opacity: 0.7, fontSize: 20, alignSelf: 'center' }} />
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease-in-out',
                          borderRadius: 2,
                          pr: 4, // Space for expand icon
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                          '&:hover fieldset': { borderColor: primaryColor },
                        },
                        // Hide scrollbar when collapsed, show when expanded
                        '& .MuiInputBase-root': {
                          overflow: expandedFields[index] ? 'auto' : 'hidden',
                        },
                        '& .MuiInputBase-input': {
                          overflow: expandedFields[index] ? 'auto' : 'hidden',
                          textOverflow: expandedFields[index] ? 'clip' : 'ellipsis',
                          whiteSpace: expandedFields[index] ? 'pre-wrap' : 'nowrap',
                        }
                      }}
                    />
                    <IconButton 
                      size="small"
                      onClick={() => toggleDescriptionExpand(index)}
                      sx={{ 
                        position: 'absolute', 
                        right: 8, 
                        top: 8,
                        color: primaryColor,
                        backgroundColor: alpha(primaryColor, 0.05),
                        '&:hover': { 
                          backgroundColor: alpha(primaryColor, 0.15) 
                        },
                        zIndex: 1
                      }}
                    >
                      {expandedFields[index] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </ClickAwayListener>
              </Grid>
              <Grid item xs={2} sm={2} sx={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, pt: isMobile ? 1.5 : 1, order: { xs: 2, sm: 3 } }}>
                <Tooltip title={t('common.save') || t('settings.save_tags')}>
                  <span>
                    <IconButton
                      onClick={() => handleSaveTag(index)}
                      disabled={savingIndex !== null || !hasTagChanged(index)}
                      size="small"
                      sx={{
                        color: primaryColor,
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid',
                        borderColor: 'rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(primaryColor, 0.08),
                          transform: 'scale(1.1)',
                          borderColor: alpha(primaryColor, 0.2),
                        },
                        '&.Mui-disabled': {
                          color: alpha(primaryColor, 0.3),
                          backgroundColor: 'rgba(255, 255, 255, 0.35)',
                        }
                      }}
                    >
                      {savingIndex === index ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('common.delete') || "Delete"}>
                  <IconButton
                    onClick={() => handleDeleteTag(index)}
                    disabled={savingIndex !== null}
                    size="small"
                    sx={{
                      color: 'error.main',
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        transform: 'scale(1.1)',
                        borderColor: alpha(theme.palette.error.main, 0.2),
                      }
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </StyledTagPaper>
        </Grow>
      ))}

      <Box sx={{ mt: 3, mb: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={handleAddTag}
          disabled={savingIndex !== null || isChanged}
          sx={{
            py: 1,
            borderRadius: '50%',
            boxShadow: '0 4px 14px rgba(87, 87, 176, 0.3)',
            width: 56,
            height: 56,
            minWidth: 56,
            backgroundColor: primaryColor,
            '&:hover': {
              backgroundColor: hoverColor,
              transform: 'scale(1.1) rotate(90deg)',
              boxShadow: '0 6px 20px rgba(87, 87, 176, 0.4)',
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(primaryColor, 0.3),
              color: 'rgba(255, 255, 255, 0.5)',
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          }}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </Button>
      </Box>

      <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', opacity: 0.8, lineHeight: 1.6 }}>
        {t('settings.tags_save_note')}
      </Typography>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Fade>
  );
};

export default SettingIntelligentTag;
